import { NextPageContextEnhanced } from "../../../types/page";
import FileCache from "./FileCache.class";
import MemoryCache from "./MemoryCache.class";
import { isBrowser } from "utils/nookies/utils";

type TypeCacheStrategy = "file" | "memory";

class CacheManager {
  static _instances = {};

  private readonly _context: NextPageContextEnhanced;
  private readonly cacheDriver: MemoryCache | FileCache;

  static getInstance(
    context: NextPageContextEnhanced,
    strategy: TypeCacheStrategy = process.env
      .CACHE_STRATEGY as TypeCacheStrategy
  ): CacheManager {
    let { lang, countryCode } = context;
    countryCode = countryCode || "xx";
    if (!CacheManager._instances?.[lang]?.[countryCode]) {
      CacheManager._instances[lang] = CacheManager._instances[lang] || {};
      CacheManager._instances[lang][countryCode] =
        CacheManager._instances[lang][countryCode] || {};
      CacheManager._instances[lang][countryCode] = new CacheManager(
        context,
        strategy
      );
    }
    return CacheManager._instances[lang][countryCode];
  }

  private constructor(context, strategy: TypeCacheStrategy) {
    const CacheDriverClass =
      strategy === "memory" || isBrowser() ? MemoryCache : FileCache;
    this._context = context;
    this.cacheDriver = new CacheDriverClass(context);
  }

  public async fetchWithCache(
    url: string,
    lang: string,
    fetchFunc,
    headers,
    cacheTimeInSeconds: number | string,
    mapper = null,
    forceFresh: boolean = false
  ) {
    if (!cacheTimeInSeconds) {
      console.log("FetchWithoutCache:", {
        url: decodeURI(url),
      });
      let response = await fetchFunc(url, { headers });
      const data = mapper ? mapper(response.data) : response.data;
      return data;
    }
    const cacheKey = `${lang}` + url.replace(`${process.env.API_URL}`, "");
    if (!forceFresh) {
      const hasValidKey = await this.hasKey(cacheKey);
      if (hasValidKey) {
        return await this.get(cacheKey);
      }
    }
    // fetch the api if no data exists on cache
    console.log("FetchWithCache:", {
      validInMinutes: Number(cacheTimeInSeconds) / 60,
      url: decodeURI(url),
    });

    let { data } = await fetchFunc(url, { headers });
    data = mapper ? mapper(data) : data;
    await this.set(cacheKey, data, cacheTimeInSeconds);
    return data;
  }

  private generateKey(key) {
    let { lang, countryCode } = this._context;
    countryCode = countryCode || "xx";
    return `${lang}-${this._context.getData("source")}-${countryCode}-${key}`;
  }

  public get(key, keyGen = null) {
    keyGen = keyGen ?? this.generateKey.bind(this);
    return this.cacheDriver.get(keyGen(key));
  }

  public set(
    key,
    content,
    cacheTimeInSeconds: string | number = null,
    keyGen = null
  ) {
    keyGen = keyGen ?? this.generateKey.bind(this);
    cacheTimeInSeconds = parseInt(
      cacheTimeInSeconds?.toString() ?? process.env.DEFAULT_API_CACHE
    );
    if (cacheTimeInSeconds) {
      return this.cacheDriver.set(keyGen(key), content, cacheTimeInSeconds);
    }
  }

  public hasKey(key, keyGen = null) {
    keyGen = keyGen ?? this.generateKey.bind(this);
    return this.cacheDriver.hasKey(keyGen(key));
  }

  public getAll(...args) {
    return this.cacheDriver.getAll.apply(null, args);
  }

  public clear(...args) {
    return this.cacheDriver.clear.apply(null, args);
  }
}

export default CacheManager;
